import { t } from "@/lib/i18n/interpolate";
import { getLocaleMeta } from "@/lib/i18n/locales";
import { escapeHtml } from "@/lib/tool-kit/citation-parts";
import {
  countPlaceholders,
  placeholder,
  segmentsOf,
  strong,
  toPlain,
} from "@/lib/tool-kit/correspondence/compose";
import { tidy } from "@/lib/tool-kit/essay/sentence";
import { countWords } from "@/lib/tool-kit/essay-templates/text";

/**
 * A formal letter in US full block, US modified block, or UK block layout, with the
 * salutation and the sign-off matched.
 *
 * Layouts (the conventions business-writing guides agree on):
 * - **US full block.** Every line starts at the left margin. Single spaced, a blank line
 *   between parts and paragraphs, no paragraph indents.
 * - **US modified block.** The sender's address, the date, the closing and the signature
 *   start at the center; the recipient's address, salutation and body stay at the left.
 * - **UK block.** Everything at the left margin, no full stops in Mr, Mrs, Ms or Dr, the
 *   date as day–month–year ("12 March 2026"), and a comma after the salutation and the
 *   close — or, in "open punctuation", no comma after either.
 *
 * Sign-offs: US letters close "Sincerely," (or "Respectfully," "Best regards," "Yours
 * truly,") whether or not the writer knows the recipient's name. UK letters follow the
 * rule: a salutation with a name ("Dear Ms Rivera") closes "Yours sincerely,"; one
 * without ("Dear Sir or Madam") closes "Yours faithfully,".
 */

export type LetterFormat = "usFull" | "usModified" | "uk";
export const LETTER_FORMATS: readonly LetterFormat[] = [
  "usFull",
  "usModified",
  "uk",
];

export type SalutationKind = "named" | "unnamed";
export type UnnamedKind = "sirMadam" | "role" | "whom";
export const UNNAMED_KINDS: readonly UnnamedKind[] = [
  "sirMadam",
  "role",
  "whom",
];

export type Honorific = "Mr" | "Ms" | "Mrs" | "Mx" | "Dr" | "Prof";
export const HONORIFICS: readonly Honorific[] = [
  "Mr",
  "Ms",
  "Mrs",
  "Mx",
  "Dr",
  "Prof",
];

export type UsSignoff = "sincerely" | "respectfully" | "regards" | "truly";
export const US_SIGNOFFS: readonly UsSignoff[] = [
  "sincerely",
  "respectfully",
  "regards",
  "truly",
];

export type LetterPurpose =
  | "inquiry"
  | "complaint"
  | "application"
  | "reference"
  | "thanks";
export const LETTER_PURPOSES: readonly LetterPurpose[] = [
  "inquiry",
  "complaint",
  "application",
  "reference",
  "thanks",
];

export interface LetterInput {
  format: LetterFormat;
  purpose: LetterPurpose;
  /** The writer's name, typed under the signature. */
  senderName: string;
  /** The writer's address, one line per line, with any phone or email last. */
  senderAddress: string;
  /** ISO date, "2026-03-12", from a date field; empty until chosen. */
  date: string;
  recipientName: string;
  recipientTitle: string;
  recipientOrganization: string;
  recipientAddress: string;
  salutationKind: SalutationKind;
  honorific: Honorific;
  /** The name after the honorific, usually the surname. */
  salutationName: string;
  unnamed: UnnamedKind;
  /** "Hiring Manager", "Admissions Committee": after "Dear". */
  role: string;
  subject: string;
  opening: string;
  details: string;
  closing: string;
  usSignoff: UsSignoff;
  enclosures: string;
  copies: string;
  /**
   * UK only: drop the comma after the salutation and the complimentary close ("open
   * punctuation"). Both forms are standard; the choice is house style.
   */
  openPunctuation: boolean;
}

export interface LetterBlock {
  /** Lines of the block; marks from the correspondence kit for blanks and bold. */
  lines: string[];
  /** Starts at the center in a modified-block letter. */
  indent: boolean;
}

/** The fixed words of a letter — translatable. */
export interface LetterWords {
  placeholders: {
    senderAddress: string;
    date: string;
    recipientName: string;
    recipientAddress: string;
    salutationName: string;
    role: string;
    subject: string;
    senderName: string;
    opening: string;
    details: string;
    closing: string;
  };
  salutation: {
    dear: string;
    sirMadam: string;
    whom: string;
    /**
     * The named salutation, `{dear} {honorific} {name}` in English. A language that
     * puts the title after the name (Japanese, Korean, Chinese) reorders it, and one
     * that needs no opening word (French "Madame Rivera") leaves `{dear}` empty.
     */
    format: string;
    /** The opening word per honorific, so a gendered "Sehr geehrte/r" can agree. */
    dearNamed: Readonly<Record<Honorific, string>>;
    /** The title as written in the salutation. Empty when `dearNamed` carries it. */
    honorifics: Readonly<Record<Honorific, string>>;
    /**
     * Appended to a title in a US-style letter ("Mr" becomes "Mr."). English only: a
     * language whose abbreviations always carry their own stop writes it in `honorifics`.
     */
    honorificStop: string;
  };
  signoffs: {
    us: Readonly<Record<UsSignoff, string>>;
    ukNamed: string;
    ukUnnamed: string;
  };
  subjectPrefix: string;
  enclosures: string;
  copies: string;
  /** Per purpose: the prompts shown for an empty body paragraph. */
  bodyPrompts: Readonly<
    Record<LetterPurpose, { opening: string; details: string; closing: string }>
  >;
}

/* ------------------------------------------------------------------ date */

const RE_TRAILING_COMMA = /,$/;
const RE_SPACES = / {2,}/g;
const RE_ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * "2026-03-12" as "March 12, 2026" (US) or "12 March 2026" (UK) on an English page, and
 * in the page language's own order and month names on any other ("12 mars 2026"). On an
 * English page the letter's region, not the page's language, decides the order: the two
 * styles are the convention being taught. Null for anything that is not a real calendar
 * date.
 */
export function formatLetterDate(
  iso: string,
  format: LetterFormat,
  locale = "en"
): string | null {
  const match = RE_ISO_DATE.exec(iso.trim());
  if (!match) {
    return null;
  }
  const [year, month, day] = [match[1], match[2], match[3]].map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  };
  const tag = getLocaleMeta(locale)?.hreflang ?? "en";
  if (tag !== "en") {
    try {
      return new Intl.DateTimeFormat(tag, options).format(date);
    } catch {
      // An Intl build without this language's data falls back to the English style.
    }
  }
  return new Intl.DateTimeFormat(
    format === "uk" ? "en-GB" : "en-US",
    options
  ).format(date);
}

/* ------------------------------------------------------------------ salutation and sign-off */

/**
 * "Mr." in a US letter, "Mr" in a UK letter. With `words`, the title in the page's
 * language ("Madame", "Herr"); an empty form means the opening word carries the title.
 */
export function honorificText(
  honorific: Honorific,
  format: LetterFormat,
  words?: LetterWords
): string {
  const form = words ? words.salutation.honorifics[honorific] : honorific;
  if (form === "" || format === "uk") {
    return form;
  }
  return `${form}${words ? words.salutation.honorificStop : "."}`;
}

/** Colon after a US salutation, comma after a UK one. */
export function salutationPunctuation(
  format: LetterFormat,
  openPunctuation = false
): string {
  if (format === "uk") {
    return openPunctuation ? "" : ",";
  }
  return ":";
}

const clean = tidy;

/** The opening word and what follows it; a language with no opening word leaves it out. */
function openWith(dear: string, rest: string): string {
  return dear === "" ? rest : `${dear} ${rest}`;
}

/** The salutation line, with its punctuation. Blank names become placeholders. */
export function buildSalutation(
  input: LetterInput,
  words: LetterWords
): string {
  const mark = salutationPunctuation(input.format, input.openPunctuation);
  if (input.salutationKind === "named") {
    const name = clean(input.salutationName);
    const shown =
      name === "" ? placeholder(words.placeholders.salutationName) : name;
    const line = t(words.salutation.format, {
      dear: words.salutation.dearNamed[input.honorific],
      honorific: honorificText(input.honorific, input.format, words),
      name: shown,
    });
    // An empty opening word or title leaves a stray space behind.
    return `${line.replace(RE_SPACES, " ").trim()}${mark}`;
  }
  switch (input.unnamed) {
    case "sirMadam":
      return `${openWith(words.salutation.dear, words.salutation.sirMadam)}${mark}`;
    case "whom":
      return `${words.salutation.whom}${mark}`;
    default: {
      const role = clean(input.role);
      const shown = role === "" ? placeholder(words.placeholders.role) : role;
      return `${openWith(words.salutation.dear, shown)}${mark}`;
    }
  }
}

/**
 * The complimentary close matched to the salutation. A UK letter's close is fixed by
 * whether the writer used a name; a US letter's is the writer's choice.
 */
export function buildSignoff(input: LetterInput, words: LetterWords): string {
  if (input.format === "uk") {
    const close =
      input.salutationKind === "named"
        ? words.signoffs.ukNamed
        : words.signoffs.ukUnnamed;
    return input.openPunctuation ? close.replace(RE_TRAILING_COMMA, "") : close;
  }
  return words.signoffs.us[input.usSignoff];
}

/* ------------------------------------------------------------------ blocks */

function lines(text: string): string[] {
  return text
    .split("\n")
    .map(clean)
    .filter((line) => line !== "");
}

function orPlaceholder(text: string, label: string): string {
  return clean(text) === "" ? placeholder(label) : clean(text);
}

function senderBlock(input: LetterInput, words: LetterWords): string[] {
  const address = lines(input.senderAddress);
  return address.length === 0
    ? [placeholder(words.placeholders.senderAddress)]
    : address;
}

function recipientBlock(input: LetterInput, words: LetterWords): string[] {
  const parts = [
    orPlaceholder(input.recipientName, words.placeholders.recipientName),
    ...[input.recipientTitle, input.recipientOrganization]
      .map(clean)
      .filter((line) => line !== ""),
  ];
  const address = lines(input.recipientAddress);
  return [
    ...parts,
    ...(address.length === 0
      ? [placeholder(words.placeholders.recipientAddress)]
      : address),
  ];
}

function bodyBlock(text: string, prompt: string): LetterBlock {
  const paragraph = clean(text);
  return {
    lines: [paragraph === "" ? placeholder(prompt) : paragraph],
    indent: false,
  };
}

/** The recipient's name is optional in a named salutation; the subject is optional too. */
function subjectBlock(
  input: LetterInput,
  words: LetterWords
): LetterBlock | null {
  const subject = clean(input.subject);
  if (subject === "") {
    return null;
  }
  const line =
    input.format === "uk"
      ? strong(subject)
      : strong(`${words.subjectPrefix} ${subject}`.trim());
  return { lines: [line], indent: false };
}

/** The letter as blocks, top to bottom. Each block is followed by a blank line. */
export function buildLetter(
  input: LetterInput,
  words: LetterWords,
  locale = "en"
): LetterBlock[] {
  const modified = input.format === "usModified";
  const dateText =
    formatLetterDate(input.date, input.format, locale) ??
    placeholder(words.placeholders.date);
  const prompts = words.bodyPrompts[input.purpose];
  const blocks: LetterBlock[] = [
    { lines: senderBlock(input, words), indent: modified },
    { lines: [dateText], indent: modified },
    { lines: recipientBlock(input, words), indent: false },
    { lines: [buildSalutation(input, words)], indent: false },
  ];
  const subject = subjectBlock(input, words);
  if (subject) {
    blocks.push(subject);
  }
  blocks.push(
    bodyBlock(input.opening, prompts.opening),
    bodyBlock(input.details, prompts.details),
    bodyBlock(input.closing, prompts.closing),
    {
      lines: [
        buildSignoff(input, words),
        "",
        "",
        "",
        orPlaceholder(input.senderName, words.placeholders.senderName),
      ],
      indent: modified,
    }
  );
  const enclosures = clean(input.enclosures);
  const copies = clean(input.copies);
  const notation = [
    enclosures === "" ? "" : `${words.enclosures} ${enclosures}`,
    copies === "" ? "" : `${words.copies} ${copies}`,
  ].filter((line) => line !== "");
  if (notation.length > 0) {
    blocks.push({ lines: notation, indent: false });
  }
  return blocks;
}

/* ------------------------------------------------------------------ output */

/** Columns of indentation a plain-text copy uses for center-aligned blocks. */
export const TEXT_INDENT = 36;

/** Plain text: blocks separated by a blank line, center-aligned blocks indented. */
export function letterToText(blocks: readonly LetterBlock[]): string {
  const pad = " ".repeat(TEXT_INDENT);
  return blocks
    .map((block) =>
      block.lines
        .map((line) =>
          block.indent && line !== "" ? pad + toPlain(line) : toPlain(line)
        )
        .join("\n")
    )
    .join("\n\n");
}

function runsToHtml(line: string): string {
  return segmentsOf(line)
    .map((segment) => {
      let html = escapeHtml(segment.text);
      if (segment.placeholder) {
        html = `<mark>[${html}]</mark>`;
      }
      return segment.bold ? `<b>${html}</b>` : html;
    })
    .join("");
}

/** The inner HTML of the letter: one paragraph per block, center blocks inset. */
export function letterToHtml(blocks: readonly LetterBlock[]): string {
  return blocks
    .map((block) => {
      const style = block.indent
        ? ' style="margin:0 0 12pt 3.25in"'
        : ' style="margin:0 0 12pt 0"';
      return `<p${style}>${block.lines.map(runsToHtml).join("<br>")}</p>`;
    })
    .join("");
}

/** A page for the browser's print dialog: letter-sized margins and a serif face. */
export function letterToPrintHtml(
  blocks: readonly LetterBlock[],
  title: string,
  lang: string
): string {
  return `<!doctype html><html lang="${escapeHtml(lang)}"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>@page{margin:1in}body{margin:0;font:12pt/1.3 "Times New Roman",Times,serif;color:#000}mark{background:none;color:inherit}</style></head><body>${letterToHtml(blocks)}</body></html>`;
}

/** Words in the three body paragraphs the writer typed — for the one-page check. */
export function bodyWords(input: LetterInput): number {
  return countWords([input.opening, input.details, input.closing].join(" "));
}

/** Blanks still to fill in the finished letter. */
export function letterBlanks(blocks: readonly LetterBlock[]): number {
  return countPlaceholders(blocks.flatMap((block) => block.lines));
}

/** About one single-spaced page of 12-point type, once the address blocks are counted. */
export const ONE_PAGE_BODY_WORDS = 350;
